/**
 * Research guide (E13-F03), registered as `renderView('help_research')`.
 *
 * Reachable from the "Research guide" card on `/help` (visible to every role
 * — Research itself has no access gate today, see `HelpView.tsx`) or by
 * direct URL. Same TOC + article grid as `HelpView.tsx`, just scoped to the
 * `help_research` page of the shared catalogue.
 */
import { useState } from 'react';
import { filterHelpTopicsForPage } from './help-sections.js';
import { HelpToc } from './HelpToc.js';
import { ResearchWelcome } from './sections/ResearchWelcome.js';
import { ResearchLibrary } from './sections/ResearchLibrary.js';
import { ResearchIntake } from './sections/ResearchIntake.js';
import { ResearchRun } from './sections/ResearchRun.js';
import { ResearchAiChain } from './sections/ResearchAiChain.js';
import { ResearchReview } from './sections/ResearchReview.js';
import { ResearchPublish } from './sections/ResearchPublish.js';

export interface HelpResearchViewProps {
  /** Return to the main `/help` guide (the sidebar's Help button also works). */
  readonly onBack: () => void;
}

export function HelpResearchView({ onBack }: HelpResearchViewProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const topics = filterHelpTopicsForPage('help_research', query);

  return (
    <div
      data-testid="help-research-view"
      className="grid items-start gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]"
    >
      <HelpToc page="help_research" topics={topics} query={query} onQueryChange={setQuery} />

      <article className="min-w-0 space-y-6">
        <button
          type="button"
          data-testid="help-research-back"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-text-strong"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden />
          Back to Help
        </button>
        <ResearchWelcome />
        <ResearchLibrary />
        <ResearchIntake />
        <ResearchRun />
        <ResearchAiChain />
        <ResearchReview />
        <ResearchPublish />
      </article>
    </div>
  );
}
